import { API } from '.'
import type { GetPlaceholderResponse } from './dto/mail-templates.dto'
import type { MailTemplatePlaceholder } from '@/common/mail-templates.common'

function mapInvoicePlaceholder(ph: MailTemplatePlaceholder): MailTemplatePlaceholder {
  switch (ph.name) {
    case 'customer_name':
      return {
        ...ph,
        displayName: 'Kundenname',
        description: 'der Name der Bank/des Kunden, der die Rechnung erhält',
      }

    case 'contact_person':
      return {
        ...ph,
        displayName: 'Ansprechpartner',
        description:
          'der für die Bank hinterlegte Ansprechpartner; wenn keiner hinterlegt ist, wird der Bankname verwendet (wird nur in Rechnungstexten aufgelöst)',
      }

    case 'invoice_month':
      return {
        ...ph,
        displayName: 'Rechnungsmonat',
        description: 'der Monat, für den die Rechnung erstellt wird',
      }

    case 'invoice_year':
      return {
        ...ph,
        displayName: 'Rechnungsjahr',
        description: 'das Jahr, für das die Rechnung erstellt wird',
      }

    case 'invoice_due_date':
      return {
        ...ph,
        displayName: 'Fälligkeitsdatum der Rechnung',
        description: 'das Datum, bis zu dem die Rechnung bezahlt werden muss (Format: TT.MM.JJJJ)',
      }

    case 'sib_name':
      return {
        ...ph,
        displayName: 'SIB Name',
        description: 'der Name der SIB-Organisation/des Systems',
      }

    case 'invoice_number':
      return {
        ...ph,
        displayName: 'Rechnungsnummer',
        description: 'die eindeutige Rechnungsnummer',
      }

    case 'today_date':
      return {
        ...ph,
        displayName: 'Heutiges Datum',
        description: 'das aktuelle Datum, an dem die E-Mail gesendet wird (Format: TT.MM.JJJJ)',
      }

    case 'period_text':
      return {
        ...ph,
        displayName: 'Zeitraumstext',
        description:
          'die Beschreibung des Abrechnungszeitraums (z. B. „den Zeitraum Januar – Dezember 2025“ für Aufträge oder „den Monat Dezember 2025“ für Aktivitäten)',
      }

    case 'first_name':
      return {
        ...ph,
        displayName: 'SIB Vorname',
        description: 'der Vorname des SIB-Nutzers, der die Rechnung erstellt',
      }

    case 'last_name':
      return {
        ...ph,
        displayName: 'SIB Nachname',
        description: 'der Nachname des SIB-Nutzers, der die Rechnung erstellt',
      }

    case 'month_year':
      return {
        ...ph,
        displayName: 'Monat und Jahr',
        description:
          'kombinierter Monat und Jahr aus dem Abrechnungszeitraum (Format: „MMMM JJJJ“)',
      }

    case 'invoice_date':
      return {
        ...ph,
        displayName: 'Rechnungsdatum',
        description: 'das Datum, an dem die Rechnung erstellt wurde (Format: TT.MM.JJJJ)',
      }

    case 'period_start':
      return {
        ...ph,
        displayName: 'Startdatum des Zeitraums',
        description: 'das Startdatum des Abrechnungszeitraums (Format: TT.MM.JJJJ)',
      }

    case 'period_end':
      return {
        ...ph,
        displayName: 'Enddatum des Zeitraums',
        description: 'das Enddatum des Abrechnungszeitraums (Format: TT.MM.JJJJ)',
      }

    case 'total_gross':
      return {
        ...ph,
        displayName: 'Gesamtbruttobetrag',
        description:
          'der Gesamtbruttobetrag inklusive Mehrwertsteuer (Format: Währung mit €-Symbol)',
      }

    case 'total_net':
      return {
        ...ph,
        displayName: 'Gesamtnettobetrag',
        description: 'der Gesamtnettobetrag ohne Mehrwertsteuer (Format: Währung mit €-Symbol)',
      }

    case 'tax_amount':
      return {
        ...ph,
        displayName: 'Steuerbetrag',
        description: 'der Gesamtsteuer-/Mehrwertsteuerbetrag (Format: Währung mit €-Symbol)',
      }

    case 'vat_percentage':
      return {
        ...ph,
        displayName: 'Mehrwertsteuersatz',
        description: 'der verwendete Mehrwertsteuersatz der Rechnung',
      }

    case 'sib_iban':
      return {
        ...ph,
        displayName: 'SIB IBAN',
        description: 'die IBAN (Internationale Bankkontonummer) des SIB-Nutzers für die Zahlung',
      }

    case 'sib_bic':
      return {
        ...ph,
        displayName: 'SIB BIC',
        description: 'der BIC/SWIFT-Code der Bank des SIB-Nutzers',
      }

    case 'sib_bank_name':
      return {
        ...ph,
        displayName: 'SIB Bankname',
        description: 'der Name der Bank des SIB-Nutzers für die Zahlungsinformationen',
      }

    case 'sib_tax_number':
      return {
        ...ph,
        displayName: 'SIB Steuernummer',
        description: 'die deutsche Steuernummer des SIB-Nutzers',
      }

    case 'sib_vat_id':
      return {
        ...ph,
        displayName: 'SIB USt-IdNr.',
        description: 'die EU-Umsatzsteuer-Identifikationsnummer des SIB-Nutzers',
      }

    default:
      return ph
  }
}

export function getActiveMailTemplate(customerId: number, language: string = 'de') {
  return API(
    `/settings/invoice/email-templates/active?customerId=${customerId}&language=${language}`,
    {},
    'Fehler beim Laden der E-Mail Vorlage',
    'MAIL_TEMPLATE_FETCH_ERROR'
  )
}

export async function getMailTemplatePlaceholders(): Promise<GetPlaceholderResponse> {
  const res = await API<GetPlaceholderResponse>(
    `/settings/invoice/email-templates/placeholders`,
    {},
    'Fehler beim Laden der Platzhalter',
    'MAIL_TEMPLATE_PLACEHOLDERS_FETCH_ERROR'
  )

  return {
    ...res,
    placeholders: res.placeholders.map(mapInvoicePlaceholder),
  }
}

export function createMailTemplate(
  customerId: string,
  language: string,
  subject: string,
  bodyHtml: string
) {
  return API(
    `/settings/invoice/email-templates`,
    {
      method: 'POST',
      body: JSON.stringify({ customerId, language, subject, bodyHtml }),
    },
    'Fehler beim Erstellen der E-Mail Vorlage',
    'MAIL_TEMPLATE_CREATE_ERROR'
  )
}

export function updateMailTemplate(templateId: string, subject: string, bodyHtml: string) {
  return API(
    `/settings/invoice/email-templates/${templateId}`,
    {
      method: 'PUT',
      body: JSON.stringify({ subject, bodyHtml }),
    },
    'Fehler beim Aktualisieren der E-Mail Vorlage',
    'MAIL_TEMPLATE_UPDATE_ERROR'
  )
}
